import React, { useState, useRef } from "react";
import { InputTextarea } from "primereact/inputtextarea";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Toast } from "primereact/toast";
import { ProgressBar } from "primereact/progressbar";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { apiService, CustomIdLookupResult } from "../../../api/api";
import { trackToolAction } from "../../../utils/analytics";

const CustomIdLookupTab: React.FC = () => {
  const toast = useRef<Toast>(null);

  const [videoIdInput, setVideoIdInput] = useState("");
  const [results, setResults] = useState<CustomIdLookupResult[]>([]);
  const [summary, setSummary] = useState<{
    videosProcessed: number;
    totalClaims: number;
    uniqueCustomIds: number;
  } | null>(null);
  const [loading, setLoading] = useState(false);

  const parseVideoIds = (input: string): string[] =>
    Array.from(
      new Set(
        input
          .replace(/,/g, "\n")
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );

  const handleLookup = async () => {
    const videoIds = parseVideoIds(videoIdInput);

    if (videoIds.length === 0) {
      toast.current?.show({
        severity: "warn",
        summary: "No video IDs",
        detail: "Please enter at least one YouTube video ID.",
      });
      return;
    }

    if (videoIds.length > 50) {
      toast.current?.show({
        severity: "warn",
        summary: "Too many IDs",
        detail: "Maximum 50 video IDs per request.",
      });
      return;
    }

    trackToolAction("YouTubeCustomIdLookup", "lookup");
    setLoading(true);
    setResults([]);
    setSummary(null);

    try {
      const response =
        await apiService.youtubeCustomIdLookup.lookupCustomIds(videoIds);
      setResults(response.results);
      setSummary(response.summary);
      toast.current?.show({
        severity: "success",
        summary: "Lookup complete",
        detail: `Processed ${response.summary.videosProcessed} video(s), found ${response.summary.uniqueCustomIds} unique custom ID(s).`,
        life: 5000,
      });
    } catch (err: unknown) {
      const message =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : err instanceof Error
            ? err.message
            : "Lookup failed";
      toast.current?.show({
        severity: "error",
        summary: "Lookup failed",
        detail: message,
        life: 6000,
      });
    } finally {
      setLoading(false);
    }
  };

  const exportCsv = () => {
    if (!results.length) return;
    trackToolAction("YouTubeCustomIdLookup", "export");
    const headers = [
      "video_id",
      "asset_id",
      "content_type",
      "claim_status",
      "asset_type",
      "asset_title",
      "custom_id",
    ];
    const rows = results.map((r) =>
      [
        r.videoId,
        r.assetId,
        r.contentType,
        r.claimStatus,
        r.assetType,
        r.assetTitle,
        r.customId,
      ]
        .map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "custom_id_lookup.csv";
    link.click();
    URL.revokeObjectURL(url);
  };

  const customIdBodyTemplate = (rowData: CustomIdLookupResult) => {
    const { customId } = rowData;
    if (!customId || customId === "—" || customId === "No claims found") {
      return <span style={{ color: "#888" }}>{customId}</span>;
    }
    if (customId.startsWith("ERROR:")) {
      return (
        <Tag
          value={customId}
          severity="danger"
          style={{
            maxWidth: "260px",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        />
      );
    }
    return <Tag value={customId} severity="success" />;
  };

  return (
    <div style={{ padding: "1rem 0" }}>
      <Toast ref={toast} />

      <Card title="Custom ID Lookup" style={{ marginBottom: "1.5rem" }}>
        <div className="flex flex-column gap-3">
          <div className="flex flex-column gap-2">
            <label className="font-medium text-sm">
              YouTube Video IDs{" "}
              <span className="font-normal text-color-secondary">
                (one per line or comma-separated, max 50)
              </span>
            </label>
            <InputTextarea
              value={videoIdInput}
              onChange={(e) => setVideoIdInput(e.target.value)}
              rows={6}
              placeholder={"dQw4w9WgXcQ\nabc123XYZ\n..."}
              style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
              disabled={loading}
            />
          </div>

          <div className="flex gap-2">
            <Button
              label={loading ? "Looking up…" : "Look up Custom IDs"}
              icon="pi pi-search"
              loading={loading}
              disabled={loading || !videoIdInput.trim()}
              onClick={handleLookup}
            />
            {results.length > 0 && (
              <Button
                label="Export CSV"
                icon="pi pi-download"
                severity="secondary"
                onClick={exportCsv}
                disabled={loading}
              />
            )}
          </div>

          {loading && (
            <ProgressBar mode="indeterminate" style={{ height: "4px" }} />
          )}
        </div>
      </Card>

      {summary && (
        <>
          <div className="flex gap-3 flex-wrap" style={{ marginBottom: "1rem" }}>
            <Card style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div className="text-3xl font-bold text-primary">
                {summary.videosProcessed}
              </div>
              <div className="text-sm text-color-secondary mt-1">
                Videos processed
              </div>
            </Card>
            <Card style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div className="text-3xl font-bold text-primary">
                {summary.totalClaims}
              </div>
              <div className="text-sm text-color-secondary mt-1">
                Total claims
              </div>
            </Card>
            <Card style={{ flex: "1 1 140px", textAlign: "center" }}>
              <div className="text-3xl font-bold text-green-500">
                {summary.uniqueCustomIds}
              </div>
              <div className="text-sm text-color-secondary mt-1">
                Unique custom IDs
              </div>
            </Card>
          </div>

          <Divider />

          <DataTable
            value={results}
            paginator
            rows={25}
            rowsPerPageOptions={[25, 50, 100]}
            filterDisplay="row"
            emptyMessage="No results"
            className="p-datatable-sm"
            style={{ marginTop: "0.5rem" }}
          >
            <Column
              field="videoId"
              header="Video ID"
              filter
              filterPlaceholder="Search"
              sortable
              style={{ fontFamily: "monospace", minWidth: "120px" }}
            />
            <Column
              field="assetId"
              header="Asset ID"
              filter
              filterPlaceholder="Search"
              sortable
              style={{ fontFamily: "monospace", minWidth: "120px" }}
            />
            <Column
              field="contentType"
              header="Content Type"
              sortable
              style={{ minWidth: "110px" }}
            />
            <Column
              field="claimStatus"
              header="Claim Status"
              sortable
              style={{ minWidth: "110px" }}
            />
            <Column
              field="assetType"
              header="Asset Type"
              sortable
              style={{ minWidth: "110px" }}
            />
            <Column
              field="assetTitle"
              header="Asset Title"
              filter
              filterPlaceholder="Search"
              sortable
              style={{ minWidth: "180px" }}
            />
            <Column
              field="customId"
              header="Custom ID"
              body={customIdBodyTemplate}
              filter
              filterPlaceholder="Search"
              sortable
              style={{ minWidth: "160px" }}
            />
          </DataTable>
        </>
      )}
    </div>
  );
};

export default CustomIdLookupTab;
